import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { finalize, Observable } from 'rxjs';
import {
  ActiveIconComponent,
  AddIconComponent,
  AllIconComponent,
  CloseCircleIconComponent,
  CloseSquareIconComponent,
  DeleteIconComponent,
  OrgChartIconComponent,
  RowIconComponent,
  TickSquareIconComponent,
} from '@kuiper/icons';
import { DepartmentTreeItem } from '../department-model';
import { DepartmentService } from '../department.service';
import {
  ACTIONS_INDEXES,
  ACTIONS_MENU_ITEMS,
  DEPARTMENT_STATE_TABS,
  DEPARTMENT_TREE_PAGE_SIZES,
} from './departments.constant';
import { DepartmentsStore } from './departments.store';
import { DepartmentsFlatListComponent } from './departments-flat-list.component';
import { DepartmentTreeTableComponent } from './department-tree-table/department-tree-table.component';
import { DepartmentOrgChartComponent } from './department-org-chart/department-org-chart.component';
import { DepartmentQuickViewComponent } from './department-quick-view/department-quick-view.component';
import { ActionListComponent } from '../../../../../common/components/action-list/action-list.component';
import { OffCanvasComponent } from '../../../../../common/components/off-canvas/off-canvas.component';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import { FeatureFlagsService } from '../../../../../common/services/feature-flags/feature-flags.service';
import { ADMIN_ROUTES } from '../../../../../common/constants/admin.routing.constants';
import { FEATURE_FLAGS } from '../../../../../common/constants/feature-flags.constants';
import { FLOATING_PAGE_CLASSES } from '../../../../../common/helpers/system-tabs-layout/system-tabs-layout';
import { TenantPathPipe } from '../../../../../common/pipes/tenant-path/tenant-path.pipe';
import { UserStatusPipe } from '../../../../../common/pipes/user-status/user-status.pipe';
import { ActionList } from '../../../../../common/models/action-list.model';
import { AvatarPipe } from '../../../../../../ui-components/pipes/avatar/avatar.pipe';
import { BadgeComponent } from '../../../../../../ui-components/lib/badge/badge.component';
import { SearchFieldComponent } from '../../../../../../ui-components/lib/search-field/search-field.component';
import { initialSearchTerm } from '../../../../../../ui-components/lib/search-field/search.service';
import { LoaderComponent } from '../../../../../../ui-components/lib/loader/loader.component';
import { ModalServiceService } from '../../../../../../ui-components/lib/modal/modal-service.service';
import { ToasterService } from '../../../../../../ui-components/services/toast/toast.service';

@Component({
  selector: 'lib-departments',
  imports: [
    NgTemplateOutlet,
    TranslocoPipe,
    RouterModule,
    TenantPathPipe,
    UserStatusPipe,
    AvatarPipe,
    BadgeComponent,
    ActionListComponent,
    OffCanvasComponent,
    SearchFieldComponent,
    LoaderComponent,
    ActiveIconComponent,
    AddIconComponent,
    AllIconComponent,
    CloseCircleIconComponent,
    CloseSquareIconComponent,
    DeleteIconComponent,
    OrgChartIconComponent,
    RowIconComponent,
    TickSquareIconComponent,
    DepartmentsFlatListComponent,
    DepartmentTreeTableComponent,
    DepartmentOrgChartComponent,
    DepartmentQuickViewComponent,
  ],
  providers: [DepartmentsStore],
  templateUrl: './departments.component.html',
  styleUrl: './departments.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DepartmentsComponent implements OnInit {
  readonly ADMIN_ROUTES = ADMIN_ROUTES;

  protected readonly store = inject(DepartmentsStore);
  private readonly router = inject(Router);
  private readonly departmentService = inject(DepartmentService);
  private readonly transloco = inject(TranslocoService);
  private readonly tenantService = inject(TenantService);
  private readonly featureFlagService = inject(FeatureFlagsService);
  private readonly modalService = inject(ModalServiceService);
  private readonly toaster = inject(ToasterService);
  private readonly destroyRef = inject(DestroyRef);

  readonly deactivateModal = viewChild<TemplateRef<unknown>>('deactivate');
  readonly activateModal = viewChild<TemplateRef<unknown>>('activate');
  readonly deleteModal = viewChild<TemplateRef<unknown>>('deleteRecord');

  protected readonly stateTabs = DEPARTMENT_STATE_TABS;
  protected readonly pageSizes = DEPARTMENT_TREE_PAGE_SIZES;

  /*
   * Read directly instead of via provideSystemTabsLayout(): the tree table's lib-filtered-table
   * would otherwise draw its own card inside this page's card. The flat list (gate off) opts in itself.
   */
  protected readonly floating = computed(() =>
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.SYSTEM.system_default_color
    )
  );
  protected readonly floatingPageClasses = computed(() =>
    this.floating() ? FLOATING_PAGE_CLASSES : ''
  );
  protected readonly isNewTableUi = computed(() =>
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.TABLE.ui_enhancement
    )
  );

  readonly isDepartmentsUpdateEnabled = signal(
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.ADMIN.USER_MANAGEMENT.departments_update
    )
  ).asReadonly();

  readonly isDeleteInactiveEnabled = signal(
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.ADMIN.USER_MANAGEMENT
        .delete_inactive_groups_departments_job_titles_and_positions
    )
  ).asReadonly();

  readonly selectedDepartment = signal<DepartmentTreeItem | undefined>(
    undefined
  );
  readonly isDepartmentStateLoading = signal(false);
  private readonly restoredSearch = initialSearchTerm();

  readonly actionItemsMapped = signal<ActionList[]>(
    ACTIONS_MENU_ITEMS.map(item => ({
      ...item,
      label: this.transloco.translate(item.label),
    }))
  );

  readonly actionMenuItems = computed(() => {
    const department = this.selectedDepartment();
    const actionItemsMapped = this.actionItemsMapped();

    if (!department) {
      return [];
    }

    const actionItems: ActionList[] = [
      {
        ...actionItemsMapped[ACTIONS_INDEXES.VIEW],
        routerLinks: [
          this.tenantService.adminPath(
            `${ADMIN_ROUTES.DEPARTMENTS.DETAILS}/${department.id}`
          ),
        ],
      },
      {
        ...actionItemsMapped[ACTIONS_INDEXES.EDIT],
        hasDivider: true,
        routerLinks: [
          this.tenantService.adminPath(
            `${ADMIN_ROUTES.DEPARTMENTS.FORM}/${department.id}`
          ),
        ],
      },
    ];

    if (department.state?.value === 'active') {
      actionItems.push(actionItemsMapped[ACTIONS_INDEXES.DEACTIVATE]);
    } else if (department.state?.value === 'inactive') {
      actionItems.push(actionItemsMapped[ACTIONS_INDEXES.ACTIVATE]);
    }

    if (
      this.isDeleteInactiveEnabled() &&
      department.state?.value === 'inactive'
    ) {
      actionItems.push(actionItemsMapped[ACTIONS_INDEXES.DELETE]);
    }

    return actionItems;
  });

  ngOnInit(): void {
    this.store.setSearch(this.restoredSearch);
    if (this.isDepartmentsUpdateEnabled()) {
      this.store.load();
    }
  }

  handleAction($event: string, row: DepartmentTreeItem) {
    this.selectedDepartment.set(row);

    switch ($event) {
      case 'view':
        this.router.navigate([
          this.tenantService.adminPath(`${ADMIN_ROUTES.DEPARTMENTS.DETAILS}`),
          row.id,
        ]);
        break;
      case 'edit':
        this.router.navigate([
          this.tenantService.adminPath(`${ADMIN_ROUTES.DEPARTMENTS.FORM}`),
          row.id,
        ]);
        break;
      case 'deactivate':
        this.openModal('deactivate');
        break;
      case 'activate':
        this.openModal('activate');
        break;
      case 'delete':
        this.openModal('delete');
        break;
    }
  }

  openModal(type: 'activate' | 'deactivate' | 'delete') {
    const customizeContent =
      type === 'deactivate'
        ? this.deactivateModal()
        : type === 'activate'
          ? this.activateModal()
          : this.deleteModal();

    this.modalService.modalContent.set({
      isOpen: true,
      hasCloseIcon: false,
      customizeContent,
      width: '24',
    });
  }

  closeModal() {
    this.modalService.modalContent.set({ isOpen: false });
  }

  deactivateDepartment() {
    this.updateDepartmentState(departmentId =>
      this.departmentService.deactivateDepartment(departmentId)
    );
  }

  activateDepartment() {
    this.updateDepartmentState(departmentId =>
      this.departmentService.activateDepartment(departmentId)
    );
  }

  deleteDepartment() {
    this.updateDepartmentState(departmentId =>
      this.departmentService.deleteDepartment(departmentId)
    );
  }

  private updateDepartmentState(
    request: (departmentId: string) => Observable<unknown>
  ) {
    const departmentId = this.selectedDepartment()?.id;
    if (!departmentId) {
      return;
    }

    this.isDepartmentStateLoading.set(true);
    request(departmentId.toString())
      .pipe(
        finalize(() => this.isDepartmentStateLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.store.loadDepartments();
          this.closeModal();
        },
        error: (error: { message?: string }) => {
          this.toaster.add(error.message ?? '', '', '', 'error', true, true);
        },
      });
  }
}
